import Cookies from 'js-cookie';
import React, { SyntheticEvent, useContext, useMemo, useState } from 'react';

import { BasicCache, RestfulProvider } from '@vpr/restful';
import { FatalAttraction as FA } from '@vpr/tracking';

import { rem } from '@vimeo/bokeh';
import LoadingPage from '@vimeo/core/components/LoadingPage';
import { useQuery } from '@vimeo/core/hooks/use-api-resource';
import { useCapability } from '@vimeo/core/hooks/use-capability';
import { User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import AlbumConfigContext from './AlbumConfigContext';
import { HideByScreenWidth } from './AlbumsAppStyled';
import AlbumsModalContainer from './AlbumsModalContainer';
import { ClipsFetchError } from '../../errors';
import { ClipDownloadModalContainer } from '../components/ClipDownloadModal';
import CreatorActionBar from '../components/CreatorActionBar/CreatorActionBar';
import LoadMoreButton from '../components/LoadMoreButton/LoadMoreButton';
import OfComLoginModal from '../components/OfComLoginModal';
import SearchDispatchProvider from '../components/SearchContext';
import { EmbedShowcaseContext } from '../context/useEmbedShowcase';
import usePagination from '../hooks/usePagination';
import FeaturedLayout from '../layouts/Featured';
import GridLayout from '../layouts/Grid';
import LiveLayout from '../layouts/Live';
import Player from '../layouts/Player';
import ErrorPage from '../pages/ErrorPage';
import {
  ErrorNotification,
  VideoData,
  VimeoAlbumInfoResponse,
  ContentRating,
  Team,
} from '../types';
import {
  ALBUM_SORTS,
  albumVideosRequestFields,
  DEFAULT_SORT,
  LAYOUT_GRID,
  LAYOUT_LIVE,
  LAYOUT_PLAYER,
  LAYOUT_PLAYLIST,
  MOBILE_BREAKPOINT,
  USER_KEY,
} from '../utils/constants';
import {
  hasPreviewParam,
  idFromUri,
  isAlbumCustomized,
  convertColorForDisplay,
  getLayoutPageSize,
  getUserId,
} from '../utils/helpers';
import styleSiteFooter from '../utils/styleSiteFooter';

type ErrorHandler = (e: ErrorNotification) => void;

type TeamsResponseData = {
  items: Team[];
};

interface Props {
  albumId: number;
  albumHash?: string;
  handleError: ErrorHandler;
  curUser?: User;
  album: VimeoAlbumInfoResponse;
  hashedPass: string;
  defaultPage: number;
}

const cache = new BasicCache();

const SSO_AUTH_FLAG_COOKIE_NAME = 'vimeo_sso_auth';

function AlbumsApp({
  albumId,
  albumHash,
  handleError,
  curUser,
  album,
  hashedPass,
  defaultPage,
}: Props) {
  const { sort, hideUpcoming, layout } = album;
  const albumSort = sort && (ALBUM_SORTS as any)[sort] ? (ALBUM_SORTS as any)[sort] : DEFAULT_SORT;
  const embedShowcaseData = useContext(EmbedShowcaseContext);

  const [page, setPage] = usePagination(defaultPage, album.layout === LAYOUT_PLAYER);
  const {
    data: videoData,
    error,
    loading,
  } = useQuery<VideoData>(`/albums/${albumId}/videos`, {
    variables: {
      page,
      ...albumSort,
      fields: albumVideosRequestFields,
      per_page: getLayoutPageSize(layout),
      filter: hideUpcoming ? 'playable' : '',
      is_embed: embedShowcaseData?.isShowcaseEmbeded,
      _hashed_pass: hashedPass || '',
      referrer: embedShowcaseData?.referrer,
    },
    accept: { version: '3.4.9', resource: 'video' },
  });

  const [downloadClipId, setDownloadClipId] = useState(0);
  const { isOffsite, isOwnerOrAdmin } = useContext(AlbumConfigContext);
  const isEmbedOrOffsite = isOffsite || embedShowcaseData?.isShowcaseEmbeded;
  const viewer = useContext(ViewerContext);

  const ownerId = idFromUri(album?.user?.uri, USER_KEY);

  let teamRole: string | undefined;

  if (curUser) {
    const { data: teams } = useQuery<TeamsResponseData>(`/users/${curUser.id}/teams`, {
      variables: { fields: ['owner.uri', 'team_membership.role', 'team_data.team_showcase_id'] },
    });

    const currentTeam = teams?.items.find((team) => {
      return getUserId(team.owner.uri) === String(ownerId);
    });

    const role = ownerId === curUser?.id ? 'Owner' : currentTeam?.teamMembership?.role;

    teamRole = currentTeam?.teamData?.teamShowcaseId ? role : undefined;
  }

  const { capabilities } = useCapability(['canCustomizeAlbums', 'canCreateEmbeddedPlaylists']);

  const isUpdating = useMemo(() => {
    if (videoData) {
      return loading || videoData.page !== page;
    }
    return false;
  }, [page, videoData]);

  const videos = videoData && videoData.items;

  const isMaturedOrUnrated = useMemo(() => {
    if (videos) {
      return videos.some(
        (video) =>
          video.contentRatingClass === ContentRating.MATURE ||
          video.contentRatingClass === ContentRating.UNRATED,
      );
    }
    return false;
  }, [videos]);

  useMemo(() => {
    const just_logged_in = Cookies.get(SSO_AUTH_FLAG_COOKIE_NAME);
    if (just_logged_in === '1' && teamRole) {
      Cookies.remove(SSO_AUTH_FLAG_COOKIE_NAME, { path: '/', domain: document.domain });
      FA.trackImpression({
        container: 'team_home_page',
        component: 'team_home_page',
        keyword: 'redirected_' + teamRole.toLowerCase(),
      });
      FA.trackImpression({
        container: 'team_home_page',
        component: 'team_home_page',
        keyword: 'layout_' + layout,
      });
    }
  }, [teamRole]);

  if (error) {
    return <ErrorPage error={new ClipsFetchError()} />;
  }

  if (!videoData || !videos || !capabilities || !viewer) {
    // Page out of range error populates on videoData object
    if (videoData && videoData.errorCode === 2286) {
      if (window.location.hostname === album.domain) {
        location.assign('/');
      } else {
        location.assign(`/showcase/${albumId}`);
      }
    }
    return <LoadingPage />;
  }

  const { recaptchaSiteKey, xsrft } = viewer;
  const isOfComQualifies =
    album.privacy.view === 'anybody' && !viewer.user && viewer.ofcomQualifies;
  const { canCustomizeAlbums, canCreateEmbeddedPlaylists } = capabilities;

  const showUpgradeBanner = () => {
    return isOwnerOrAdmin && hasPreviewParam() && !canCustomizeAlbums && isAlbumCustomized(album);
  };

  const getMoreAlbumVideos = (e: SyntheticEvent) => {
    e.preventDefault();
    setPage(page + 1);
  };

  const { allowDownloads, allowShare, allowContinuousPlay, theme } = album;

  styleSiteFooter(theme === 'dark');

  const albumIsEmpty =
    !videoData || !videoData.total || videoData.total === 0 || videos.length === 0;

  const showCreatorActionBar = isOwnerOrAdmin && !embedShowcaseData?.isShowcaseEmbeded;

  return (
    // @ts-expect-error children prop not specified on component
    <RestfulProvider cache={cache}>
      {isOfComQualifies && isMaturedOrUnrated && !isOffsite ? (
        <OfComLoginModal albumId={albumId} xsrft={xsrft} />
      ) : (
        <SearchDispatchProvider>
          <AlbumsModalContainer
            album={album}
            token={xsrft}
            recaptchaSiteKey={recaptchaSiteKey}
            teamRole={teamRole}
          >
            {(modalTriggers) => (
              <ClipDownloadModalContainer
                albumId={albumId}
                clipId={downloadClipId}
                curUser={curUser}
                hashedPass={hashedPass}
                ownerId={ownerId}
                isOwnerOrAdmin={isOwnerOrAdmin}
              >
                {({ openDownloadModal }) => (
                  <>
                    {layout === LAYOUT_GRID && (
                      <GridLayout
                        albumId={albumId}
                        albumHash={albumHash}
                        albumIsEmpty={albumIsEmpty}
                        customLogo={album.customLogo}
                        webCustomLogo={album.webCustomLogo}
                        description={album.description}
                        isOwnerOrAdmin={isOwnerOrAdmin}
                        layout={album.layout}
                        name={album.name}
                        theme={album.theme}
                        videos={videos}
                        totalVideos={videoData.total}
                        page={page}
                        setPage={setPage}
                        openShare={modalTriggers.openShare}
                        allowShare={allowShare}
                        isUpdating={isUpdating}
                        curUser={curUser}
                        isOffsite={isEmbedOrOffsite}
                        allowDownloads={allowDownloads}
                        setDownloadClipId={setDownloadClipId}
                        openDownloadModal={openDownloadModal}
                        albumOwnerId={ownerId}
                        teamRole={teamRole}
                        pictures={album.pictures}
                        hasChosenThumbnail={album.hasChosenThumbnail}
                      />
                    )}
                    {layout === LAYOUT_PLAYER && (
                      <FeaturedLayout
                        albumId={albumId}
                        albumIsEmpty={albumIsEmpty}
                        brandColor={convertColorForDisplay(album.brandColor)}
                        customLogo={album.customLogo}
                        webCustomLogo={album.webCustomLogo}
                        description={album.description}
                        layout={album.layout}
                        name={album.name}
                        theme={album.theme}
                        totalVideos={videoData.total}
                        videos={videos}
                        openShare={modalTriggers.openShare}
                        allowShare={allowShare}
                        isUpdating={isUpdating}
                        LoadMoreButton={
                          <LoadMoreButton
                            videoData={videoData}
                            album={album}
                            getMoreAlbumVideos={getMoreAlbumVideos}
                            isLoadingMore={loading}
                          />
                        }
                        handleError={handleError}
                        curUser={curUser}
                        isOffsite={isEmbedOrOffsite}
                        allowDownloads={allowDownloads}
                        setDownloadClipId={setDownloadClipId}
                        openDownloadModal={openDownloadModal}
                        continuousPlay={allowContinuousPlay}
                        page={page}
                        albumOwnerId={ownerId}
                        teamRole={teamRole}
                        pictures={album.pictures}
                        hasChosenThumbnail={album.hasChosenThumbnail}
                      />
                    )}
                    {layout === LAYOUT_LIVE && (
                      <LiveLayout
                        albumIsEmpty={albumIsEmpty || videos.length === 0} // Account for hidden upcoming with 2nd condition
                        customLogo={album.customLogo}
                        webCustomLogo={album.webCustomLogo}
                        openShare={modalTriggers.openShare}
                        name={album.name}
                        curUser={curUser}
                        allowShare={allowShare}
                        videos={videos}
                        albumId={albumId}
                        brandColor={convertColorForDisplay(album.brandColor)}
                        theme={album.theme}
                        allowDownloads={allowDownloads}
                        setDownloadClipId={setDownloadClipId}
                        openDownloadModal={openDownloadModal}
                        continuousPlay={allowContinuousPlay}
                        page={page}
                        setPage={setPage}
                        totalVideos={videoData.total}
                        isUpdating={isUpdating}
                        layout={album.layout}
                        isOffsite={isEmbedOrOffsite}
                        isOwnerOrAdmin={isOwnerOrAdmin}
                        pictures={album.pictures}
                        sort={album.sort}
                        hideUpcoming={album.hideUpcoming}
                        hashedPass={hashedPass}
                        albumOwnerId={ownerId}
                        teamRole={teamRole}
                        hasChosenThumbnail={album.hasChosenThumbnail}
                      />
                    )}
                    {layout === LAYOUT_PLAYLIST && (
                      <Player
                        albumId={albumId}
                        height={showCreatorActionBar ? `calc(100vh - ${rem(60)})` : '100%'}
                        isOffsite={isOffsite}
                      />
                    )}
                    {showCreatorActionBar && (
                      <HideByScreenWidth minWidth={MOBILE_BREAKPOINT}>
                        <CreatorActionBar
                          albumId={Number(albumId)}
                          albumPrivacy={album.privacy.view}
                          canCreateEmbeddedPlaylists={!!canCreateEmbeddedPlaylists}
                          embed={album.embed}
                          isPreviewing={showUpgradeBanner()}
                          hasVideos={videoData ? videoData.total > 0 : false}
                          openShare={() => {
                            modalTriggers.openShare();
                            FA.trackEvent({
                              container: 'creator_toolbar',
                              component: 'album',
                              keyword: 'share',
                            });
                          }}
                          isOffsite={Boolean(isOffsite)}
                        />
                      </HideByScreenWidth>
                    )}
                  </>
                )}
              </ClipDownloadModalContainer>
            )}
          </AlbumsModalContainer>
        </SearchDispatchProvider>
      )}
    </RestfulProvider>
  );
}

export default AlbumsApp;
